import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageHeader from "@/components/shared/PageHeader";
import CTABlock from "@/components/shared/CTABlock";
import { fetchCaseStudies } from "@/lib/api";
import { ArrowUpRight, Lock, ShieldCheck } from "lucide-react";
import BillboardLogo from "@/components/shared/BillboardLogo";

const TIER_ACCENT = {
  "Major Label": "from-white/[0.08] to-white/[0.02]",
  "Global Tour": "from-white/[0.06] to-white/[0.02]",
  "Independent": "from-white/[0.04] to-white/[0.02]",
};

export default function CaseStudies() {
  const [items, setItems] = useState([]);
  useEffect(() => { fetchCaseStudies().then((d) => setItems(d.case_studies)).catch(() => {}); }, []);

  return (
    <div data-testid="page-case-studies">
      <PageHeader
        eyebrow="Case Studies · Featured"
        title="Featured campaigns. Rest under NDA."
        description="A small, hand-picked look at recent Prulo work. The full roster — spend, creator lineups, strategy docs, and internal reports — is held under NDA and shared with labels and management teams on a strategy call."
      />

      <section className="pt-8 pb-14 md:pt-10 md:pb-20">
        <div className="mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((cs, i) => (
            <motion.div
              key={cs.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.08 }}
            >
              <Link
                to={`/case-studies/${cs.slug}`}
                data-testid={`case-study-link-${cs.slug}`}
                className={`group relative flex flex-col h-full min-h-[380px] overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b ${TIER_ACCENT[cs.tier] || "from-white/[0.04] to-white/[0.02]"} hover:border-white/25 hover-lift glow-card`}
              >
                {cs.cover ? (
                  <div className="relative aspect-square w-full overflow-hidden bg-zinc-950">
                    <img
                      src={cs.cover}
                      alt={`${cs.artist} — ${cs.title || ""}`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/40 pointer-events-none" />
                    <div className="absolute inset-x-0 top-0 p-5 flex items-start justify-between">
                      <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.22em] text-white/90 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur-sm border border-white/15">
                        <ShieldCheck size={11} /> {cs.tier}
                      </span>
                      <div className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur-sm border border-white/15">
                        {cs.billboard && (
                          <span
                            data-testid={`cs-billboard-badge-${cs.slug}`}
                            className="inline-flex items-center gap-1 text-white/95"
                            title="Billboard-charting campaign"
                          >
                            <BillboardLogo height={11} />
                          </span>
                        )}
                        <Lock size={12} className="text-white/80" />
                      </div>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-5 flex items-center justify-between">
                      <span className="font-mono text-[10px] tracking-[0.28em] text-white/70">{cs.code}</span>
                      <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/95 text-black opacity-0 group-hover:opacity-100 transition-opacity">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div
                      aria-hidden
                      className="absolute inset-0 pointer-events-none opacity-[0.05]"
                      style={{
                        backgroundImage:
                          "repeating-linear-gradient(135deg, rgba(255,255,255,0.4) 0 1px, transparent 1px 14px)",
                      }}
                    />
                    <div
                      aria-hidden
                      className="absolute -top-24 -right-24 w-64 h-64 rounded-full pointer-events-none opacity-40"
                      style={{
                        background: "radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 60%)",
                      }}
                    />
                  </>
                )}

                <div className="relative flex-1 flex flex-col justify-between p-8">
                  <div>
                    {!cs.cover && (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                            <ShieldCheck size={11} /> {cs.tier}
                          </span>
                          <div className="flex items-center gap-2.5">
                            {cs.billboard && (
                              <span
                                data-testid={`cs-billboard-badge-${cs.slug}`}
                                className="inline-flex items-center gap-1.5 text-zinc-400 group-hover:text-white transition-colors"
                                title="Billboard-charting campaign"
                              >
                                <BillboardLogo height={11} />
                              </span>
                            )}
                            <Lock size={12} className="text-zinc-500 group-hover:text-white transition-colors" />
                          </div>
                        </div>
                        <div className="mt-8 font-mono text-xs tracking-[0.32em] text-zinc-400">{cs.code}</div>
                      </>
                    )}
                    {cs.artist ? (
                      <>
                        <div className={`${cs.cover ? "" : "mt-3"} font-display font-semibold text-2xl md:text-3xl tracking-tight text-white/95 leading-[1.1]`}>
                          {cs.artist}
                        </div>
                        {cs.title && (
                          <div className="mt-1 italic font-light text-zinc-400 text-sm">
                            "{cs.title}"
                          </div>
                        )}
                        <div className="mt-3 text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                          {cs.genre} · {cs.year} · {cs.region}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="mt-3 font-display font-semibold text-3xl tracking-tight text-white/90">
                          {cs.genre}
                        </div>
                        <div className="mt-2 text-xs font-mono uppercase tracking-[0.22em] text-zinc-500">
                          {cs.year} · {cs.region}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="mt-8 pt-5 border-t border-white/10">
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {cs.outcomes.slice(0, 3).map((o) => (
                        <span
                          key={o}
                          className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-300 px-2 py-1 rounded-full border border-white/10 bg-white/[0.03]"
                        >
                          {o}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between text-xs font-mono">
                      <span className="inline-flex items-center gap-1.5 text-zinc-500">
                        <Lock size={11} /> Details on request
                      </span>
                      <ArrowUpRight size={14} className="text-zinc-500 group-hover:text-white transition-colors" />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Under-NDA callout — signals the rest of the roster is confidential */}
        <div className="mx-auto max-w-7xl px-6 md:px-10 mt-10 md:mt-14">
          <div
            data-testid="cs-nda-callout"
            className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] p-8 md:p-10"
          >
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none opacity-[0.04]"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(135deg, rgba(255,255,255,0.5) 0 1px, transparent 1px 14px)",
              }}
            />
            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.24em] text-zinc-500 mb-3">
                  <Lock size={12} /> The rest of the roster — under NDA
                </div>
                <div className="font-display font-semibold text-2xl md:text-3xl tracking-tight text-balance">
                  Dozens more campaigns held under NDA.
                </div>
                <p className="mt-3 text-zinc-400 max-w-xl leading-relaxed">
                  Every artist, label, and management team we work with signs mutually.
                  Full case files — with chart trajectories, creator lineups, spend and
                  outcome reporting — are shared on a strategy call.
                </p>
              </div>
              <Link
                to="/contact"
                data-testid="cs-nda-cta"
                className="inline-flex items-center gap-2 bg-white text-black font-semibold rounded-full px-6 py-3.5 hover:bg-zinc-200 transition-colors self-start md:self-auto flex-none"
              >
                Request access <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <CTABlock />
    </div>
  );
}
